import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentDuplicateIcon,
  LinkIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useMobile } from '../hooks/useMobile';

const ProjectShareModal = ({
  isOpen,
  onClose,
  onCopyLink,
  projectName,
  projectUrl,
  previewImageUrl,
}) => {
  const { isMobile } = useMobile();

  const modalVariants = {
    hidden: isMobile
      ? { y: '100%', opacity: 0 }
      : { y: 20, opacity: 0, scale: 0.96 },
    visible: { y: 0, opacity: 1, scale: 1 },
    exit: isMobile
      ? { y: '100%', opacity: 0 }
      : { y: 20, opacity: 0, scale: 0.96 },
  };

  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1],
    duration: isMobile ? 0.35 : 0.22,
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 z-[50001] bg-black/50 ${
            isMobile
              ? 'flex items-end justify-center'
              : 'flex items-center justify-center p-4'
          }`}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.18 }}
          onClick={onClose}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className={`w-full bg-white dark:bg-gray-800 ${
              isMobile ? 'rounded-t-3xl' : 'max-w-md rounded-3xl shadow-2xl'
            }`}
            style={
              isMobile
                ? {
                    maxHeight: '85dvh',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                  }
                : undefined
            }
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Share project
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Preview the public link before copying it.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close share modal"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-5 space-y-5 overflow-y-auto">
              <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="aspect-[16/9] bg-gradient-to-br from-emerald-600 to-teal-700">
                  {previewImageUrl ? (
                    <img
                      src={previewImageUrl}
                      alt={projectName || 'Project preview'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <LinkIcon className="w-12 h-12 text-white/80" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Preview
                  </p>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-words">
                    {projectName || 'Untitled project'}
                  </h3>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Share link
                </label>
                <div className="flex items-stretch gap-2">
                  <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300 break-all">
                      {projectUrl}
                    </p>
                  </div>
                  <button
                    onClick={onCopyLink}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    <DocumentDuplicateIcon className="w-5 h-5" />
                    <span>Copy</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ProjectShareModal;
